import React, { useState, useEffect } from "react";
import { AgentMessagingService } from "../services/AgentMessagingService";

interface Thread {
  threadId: string;
  agentId: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  conversationHistory: Array<{
    role: string;
    content: string;
    timestamp?: string;
    messageId?: string;
  }>;
  messageCount: number;
}

const DebugThreadPage: React.FC = () => {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadThreads();
  }, []);

  const loadThreads = async () => {
    try {
      setLoading(true);
      const response = await AgentMessagingService.getAllThreadMessages();
      setThreads(response.threads || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load threads");
    } finally {
      setLoading(false);
    }
  };

  const formatMessageContent = (content: string, role: string) => {
    if (role === "assistant") {
      try {
        const parsed = JSON.parse(content);
        return (
          <div>
            <strong>Agent Response:</strong>
            {parsed.messageToUser && (
              <div style={{ marginTop: "8px", padding: "8px", backgroundColor: "#f0f0f0", borderRadius: "4px" }}>
                <strong>Message to User:</strong> {parsed.messageToUser}
              </div>
            )}
            {parsed.sourceRequests && parsed.sourceRequests.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                <strong>Source Requests:</strong>
                {parsed.sourceRequests.map((req: any, idx: number) => (
                  <div key={idx} style={{ marginLeft: "16px", fontSize: "0.9em" }}>
                    • {req.sourceId}: {req.reason}
                  </div>
                ))}
              </div>
            )}
            {parsed.actionProposals && parsed.actionProposals.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                <strong>Action Proposals:</strong>
                {parsed.actionProposals.map((prop: any, idx: number) => (
                  <div key={idx} style={{ marginLeft: "16px", fontSize: "0.9em" }}>
                    • {prop.actionId}: {prop.reason} {prop.requiresApproval ? "(requires approval)" : ""}
                  </div>
                ))}
              </div>
            )}
            {parsed.actionExecutions && parsed.actionExecutions.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                <strong>Action Executions:</strong>
                {parsed.actionExecutions.map((exec: any, idx: number) => (
                  <div key={idx} style={{ marginLeft: "16px", fontSize: "0.9em" }}>
                    • {exec.actionId}: {exec.reason}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      } catch (e) {
        return <span>{content}</span>;
      }
    }
    return <span>{content}</span>;
  };

  if (loading) {
    return <div>Loading thread debug information...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div style={{ padding: "20px" }}>
      <h1>Thread Messages & Conversation History</h1>
      <p>Total threads: {threads.length}</p>

      {threads.map((thread) => (
        <div key={thread.threadId} style={{ marginBottom: "30px", border: "1px solid #ccc", padding: "15px", borderRadius: "8px" }}>
          <h3>Thread: {thread.threadId}</h3>
          <p>
            <strong>Agent ID:</strong> {thread.agentId}
          </p>
          <p>
            <strong>User ID:</strong> {thread.userId}
          </p>
          <p>
            <strong>Created:</strong> {new Date(thread.createdAt).toLocaleString()}
          </p>
          <p>
            <strong>Updated:</strong> {new Date(thread.updatedAt).toLocaleString()}
          </p>

          {thread.conversationHistory && (
            <div>
              <h4>
                {thread.conversationHistory.length} message{thread.conversationHistory.length !== 1 ? "s" : ""} in conversation history
              </h4>

              {thread.conversationHistory && thread.conversationHistory.length > 0 && (
                <div style={{ maxHeight: "400px", overflowY: "auto", border: "1px solid #eee", padding: "10px" }}>
                  {thread.conversationHistory.map((msg: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: "15px",
                        padding: "10px",
                        backgroundColor: msg.role === "user" ? "#e3f2fd" : "#f3e5f5",
                        borderRadius: "4px",
                        borderLeft: `4px solid ${msg.role === "user" ? "#2196f3" : "#9c27b0"}`,
                      }}
                    >
                      <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                        {msg.role === "user" ? "👤 User" : "🤖 Assistant"}
                        {msg.timestamp && <span style={{ fontSize: "0.8em", color: "#666", marginLeft: "10px" }}>{new Date(msg.timestamp).toLocaleString()}</span>}
                      </div>
                      <div style={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "0.9em" }}>{formatMessageContent(msg.content, msg.role)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {(!thread.conversationHistory || thread.conversationHistory.length === 0) && <p style={{ color: "#666", fontStyle: "italic" }}>No conversation history available</p>}
        </div>
      ))}

      {threads.length === 0 && <p>No threads found.</p>}
    </div>
  );
};

export { DebugThreadPage };
